import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, LogOut, ShieldCheck, Building2, Briefcase, Handshake, Users, UserRound, type LucideIcon } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@/components/ui/Toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';
import { Text } from '@/components/ui/Text';
import { cn } from '@/lib/cn';
import { useMyContexts } from '@/lib/session/use-my-contexts';
import { useContextStore } from '@/lib/session/context-store';
import { useSwitchContext, isNoMembershipError } from '@/lib/session/use-switch-context';
import { useLogout } from '@/lib/session/use-logout';
import { RELATIONSHIP_COPY } from '@/lib/relationships/relationships.copy';
import { contextLabel, contextPersonaLabel } from '@/lib/session/context-label';
import type { Persona } from '@/lib/session/session.types';
import { SearchInput } from '@/components/ui/SearchInput';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';

/**
 * T0015 — one icon per persona, in the SAME map as the label, so the two cannot drift apart.
 * Reuses the exact icons `Sidebar.tsx` already uses for each persona's corresponding nav
 * destination (Briefcase for `/clients`, Handshake for `/providers`/Vendors, Users for
 * `/workforce`/Workers, Building2 for `agenciesNav`/`/agencies`) rather than inventing a second,
 * unrelated icon vocabulary for the same concepts. `SA` has no existing nav-icon precedent (it
 * isn't a nav destination, it's a persona) — `ShieldCheck` chosen deliberately distinct from the
 * plain `Shield` Sidebar already uses for Compliance, to avoid the two reading as the same thing.
 */
const PERSONA_ICON: Record<Persona, LucideIcon> = {
  SA: ShieldCheck,
  AGENCY: Building2,
  CLIENT: Briefcase,
  VENDOR: Handshake,
  WORKER: Users,
};

/** T0015 — one icon per persona (`PERSONA_ICON`), muted to match the subtlety of the persona
 *  label it sits beside, never louder than the bolded org name next to it.
 *  T02 — a persona outside the `Persona` union (e.g. ENTITY, passed through by normalizePersona)
 *  has no entry; it gets a neutral fallback instead of crashing the menu. */
function PersonaIcon({ persona }: { persona: Persona }) {
  const Icon: LucideIcon | undefined = PERSONA_ICON[persona];
  if (Icon === undefined) {
    return (
      <UserRound
        size={14}
        className="text-text-subtle shrink-0"
        aria-hidden="true"
        data-testid="persona-icon-fallback"
      />
    );
  }
  return <Icon size={14} className="text-text-subtle shrink-0" aria-hidden="true" />;
}

/**
 * Active-context switcher (ENT-88 R3). Mounted on the Navbar workspace button.
 * Lists the user's contexts (org name + persona), switches on select, handles the
 * FORBIDDEN "no membership" case, and offers logout.
 *
 * Store-vs-search split: the FULL list (`all`) seeds the context store and backs
 * the active-context label; the SHOWN list comes from a SECOND, debounced query
 * (`shown`) keyed on the search term so filtering happens server-side (ES-backed
 * `myContexts`). Only `all` writes the store — search results never do. With an
 * empty search term the two share the same `q: undefined` cache entry.
 */
export function ContextSwitcher() {
  const all = useMyContexts();
  const setMemberships = useContextStore((s) => s.setMemberships);
  const memberships = useContextStore((s) => s.memberships);
  const active = useContextStore((s) => s.active);
  const switchContext = useSwitchContext();
  const logout = useLogout();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  // ~250ms before the term drives the server query.
  const debounced = useDebouncedValue(query, 250);
  const [open, setOpen] = useState(false);
  const activeItemRef = useRef<HTMLDivElement | null>(null);

  // Seed the store from the full list only — never from search results.
  useEffect(() => {
    if (all.data) setMemberships(all.data);
  }, [all.data, setMemberships]);

  // T0020 — the active context must be visible on open WITHOUT scrolling, even if it's e.g. the
  // 15th of 20 rows. Radix doesn't focus/scroll to the checked item on open by default (it focuses
  // the first item), so this is done explicitly. `requestAnimationFrame`: the scrollable rows div
  // and the active item's ref both need to exist in the DOM first — Radix portals+mounts the
  // content on open, which lands in the SAME commit as this effect for a plain `useState`-driven
  // `open`, but the ref callback on the active row still needs one paint to have run.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);



  // Server-side search; empty term → q undefined → shares the `all` cache entry.
  const shown = useMyContexts(debounced.trim() || undefined);
  const shownList = shown.data ?? [];

  const activeMembership = active
    ? memberships.find((m) => m.contextTenantId === active.tenantId && m.persona === active.persona)
    : undefined;
  const activeLabel = active
    ? contextLabel(activeMembership ?? { persona: active.persona, contextTenantId: active.tenantId })
    : all.isLoading
      ? 'Loading…'
      : 'Select context';

  function handleSelect(contextTenantId: string) {
    // Kick off the switch first so `isSwitching` flips on and the overlay covers
    // the screen, THEN route to root — so the user lands on the new workspace's
    // home (not a deep page that may not exist/authorize there) without ever
    // seeing the reset → refetch flicker underneath.
    switchContext.mutate(contextTenantId, {
      onError: (e) => {
        toast.error(isNoMembershipError(e) ? RELATIONSHIP_COPY.noMembership : 'Could not switch context.');
      },
    });
    void navigate({ to: '/' });
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className="flex items-center gap-1.5 text-sm font-semibold text-text hover:text-primary transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
        aria-label="Switch context"
      >
        <span className="max-w-[200px] truncate">{activeLabel}</span>
        <ChevronDown size={13} className="text-text-subtle" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[260px] flex flex-col max-h-[var(--radix-dropdown-menu-content-available-height)]"
      >
        {/* T0020 — capping the OUTER Content's own height to Radix's `--radix-dropdown-menu-
            content-available-height` (the real distance to the nearest viewport edge, live-
            updated by the Popper positioner) is what makes the header/search/logout genuinely
            pinned at a short viewport: capping only the scrollable rows div below, as an earlier
            pass here did, still let the WHOLE content box grow taller than the viewport and get
            clipped by the browser edge instead of scrolling internally — found by resizing to a
            short viewport and actually opening the menu, not assumed. Header/search/logout stay
            `shrink-0` below (their natural size, never compressed); the scrollable rows div is
            `flex-1 min-h-0` so it's the ONE thing that gives up space when the container is short
            (`min-h-0` overrides a flex item's content-based auto min-height, the same L022 shape
            documented elsewhere in this file — without it a flex child won't shrink below its own
            content size, which is exactly what needs to happen here for the pin to hold).
            Everything above this point (label, error retry, search) stays PINNED, always visible,
            never inside the scroll region below. */}
        <DropdownMenuLabel>
          <Text variant="label-sm" color="muted">Acting as</Text>
        </DropdownMenuLabel>

        {all.isError && (
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); all.refetch(); }}>
            <Text variant="body-sm" color="danger">{RELATIONSHIP_COPY.loadError}</Text>
          </DropdownMenuItem>
        )}

        {/* ENT-566 round-4 — always rendered, not gated on membership count: anyone who may
            discover a non-member (acting-as) context via a search needle (BE:
            searchMyContexts, auth.service.ts:141-203) must always get the entry, and the only
            way to know who that is would be a persona check this component doesn't need — BE
            remains the sole authority on what a given needle resolves to. */}
        <div className="px-2 py-1.5 shrink-0">
          <div className="flex items-center rounded-md border border-border bg-surface-elevated px-2 py-1.5">
            <SearchInput
              variant="flush"
              value={query}
              onValueChange={setQuery}
              // Stop Radix's menu type-ahead from swallowing keystrokes.
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search contexts…"
              aria-label="Search contexts"
            />
          </div>
        </div>

        {/* T0020 — ONLY the context rows scroll. `flex-1 min-h-0 overflow-y-auto`: this is the
            one flex child that gives up height when the outer Content is capped short (see the
            Content-level comment above for why capping only THIS div isn't enough on its own).
            `max-h-[320px]` is a sensible ceiling for a tall viewport, so the list doesn't grow
            unreasonably large just because the screen has room — `flex-1`/`min-h-0` are what
            actually do the work of shrinking it at a short viewport. Keyboard arrow-key
            scroll-into-view needs no extra code: Radix moves DOM focus to the highlighted item,
            and the browser's default focus behavior scrolls a focused element into view within
            its nearest scrollable ancestor — verified by keyboard, not assumed (see the task
            doc). The active item is scrolled into view on OPEN separately (via `activeItemRef` +
            the effect above), since Radix focuses the first item on open, not the checked one. */}
        <div className="flex-1 min-h-0 overflow-y-auto max-h-[320px]">
          {shownList.map((m) => {
            const isActive = active?.tenantId === m.contextTenantId && active?.persona === m.persona;
            // `myContexts` returns only active contexts (no `status`); stay
            // defensive for any future suspended/ended rows the BE might surface.
            const disabled = m.status != null && m.status !== 'ACTIVE';
            return (
              <DropdownMenuItem
                key={`${m.contextTenantId}:${m.persona}`}
                ref={isActive ? activeItemRef : undefined}
                disabled={disabled}
                onSelect={(e) => {
                  // Disabled rows: block selection, keep the menu open.
                  if (disabled) {
                    e.preventDefault();
                    return;
                  }
                  // Switch only when picking a different context; either way let the
                  // menu close (no preventDefault) so it dismisses on select.
                  if (!isActive) handleSelect(m.contextTenantId);
                }}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-none border-b border-border/30 last:border-0',
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <PersonaIcon persona={m.persona} />
                  <span className="flex min-w-0 flex-col">
                    {/* T0015 — font-weight on a CHILD span, not the Text's own `weight` prop: same
                        reason as CriticalSignalsCard's entity name (T0010 #1) — `.text-body-sm` in
                        index.css is unlayered CSS and beats any `font-*` utility (including what
                        `weight="bold"` emits) placed on the SAME element. Same workaround, same
                        root cause, not a second, independently-invented fix — the systemic fix
                        (layering index.css) is still the owner's call, unresolved as of T0010.
                        T0019 — font-semibold (600), not font-bold (700): one weight for "name in a
                        list row" across this whole pass, matching CriticalSignalsCard's entity name
                        (the only other place this pattern was used). Only the value moved. */}
                    <Text variant="body-sm" as="span" className="truncate">
                      <span className="font-semibold">{contextLabel(m)}</span>
                    </Text>
                    <Text variant="label-sm" color="subtle" as="span">
                      {disabled
                        ? `${contextPersonaLabel(m.persona)} · ${m.status!.toLowerCase()}`
                        : contextPersonaLabel(m.persona)}
                    </Text>
                  </span>
                </span>
                {isActive && <Check size={14} className="text-primary shrink-0" aria-label="Active" />}
              </DropdownMenuItem>
            );
          })}

          {!shown.isLoading && shownList.length === 0 && (
            <div className="px-3 py-2">
              <Text variant="body-sm" color="muted">No matching contexts.</Text>
            </div>
          )}
        </div>

        {/* T0020 — pinned at the foot, always visible, never inside the scroll region above. */}
        <DropdownMenuSeparator className="shrink-0" />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); logout.mutate(); }}
          className="flex items-center gap-2 shrink-0"
        >
          <LogOut size={14} className="text-text-subtle" />
          <Text variant="body-sm" as="span">Log out</Text>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
